import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { CalendarDays, MessageSquare, ListChecks, AlertCircle, Paperclip } from 'lucide-react';
import clsx from 'clsx';
import type { CardRow } from '../types';

const PRIORITY_COLORS: Record<string, string> = {
  low: '#9ca3af',
  med: '#3b82f6',
  high: '#f59e0b',
  critical: '#ef4444',
};

interface TaskCardProps {
  card: CardRow & {
    checklistDone?: number;
    checklistTotal?: number;
    commentsCount?: number;
    attachmentsCount?: number;
    tags?: string[];
  };
  onClick: () => void;
}

export function TaskCard({ card, onClick }: TaskCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { type: 'card', card },
  });

  const dueClose = card.dueDate
    ? new Date(card.dueDate).getTime() - Date.now() < 1000 * 60 * 60 * 24 * 2
    : false;

  const dueOverdue = card.dueDate ? new Date(card.dueDate).getTime() < Date.now() : false;

  const accent = PRIORITY_COLORS[card.priority] ?? PRIORITY_COLORS.med;
  const tags = card.tags ?? [];

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
      }}
      className={clsx(
        'group/card relative rounded-md bg-surface-light dark:bg-surface3-dark',
        'shadow-board-rest hover:shadow-board-hover transition-shadow',
        'cursor-grab active:cursor-grabbing',
        'overflow-hidden',
        isDragging && 'opacity-30',
      )}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        if (isDragging) return;
        e.stopPropagation();
        onClick();
      }}
    >
      {/* Цветная top-полоска по приоритету (стиль Kaiten) */}
      <span
        className="absolute left-0 right-0 top-0 h-[3px]"
        style={{ background: accent }}
        title={`Приоритет: ${card.priority}`}
      />

      {/* ASAP-индикатор */}
      {card.asap && (
        <span className="absolute top-1.5 right-1.5 inline-flex items-center justify-center text-[9px] font-bold uppercase tracking-wider px-1 py-px rounded-sm bg-red-500 text-white">
          ASAP
        </span>
      )}

      <div className="px-2 pt-2 pb-1.5">
        {/* Title */}
        <div className="text-[12.5px] leading-snug font-medium text-ink-light dark:text-ink-dark line-clamp-3">
          {card.title}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="inline-flex items-center px-1.5 py-px rounded-sm text-[10px] bg-accent/10 text-accent"
                style={{ borderRadius: 3 }}
              >
                {t}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-[10px] text-ink-faint_l dark:text-ink-faint_d">+{tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Footer с иконками */}
        {(card.dueDate ||
          (card.checklistTotal && card.checklistTotal > 0) ||
          (card.commentsCount && card.commentsCount > 0) ||
          (card.attachmentsCount && card.attachmentsCount > 0) ||
          card.estimate != null) && (
          <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-ink-faint_l dark:text-ink-faint_d">
            {card.dueDate && (
              <span
                className={clsx(
                  'inline-flex items-center gap-0.5',
                  dueOverdue
                    ? 'text-red-500 font-medium'
                    : dueClose
                      ? 'text-amber-500'
                      : '',
                )}
              >
                <CalendarDays size={11} strokeWidth={2} />
                {new Date(card.dueDate).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: '2-digit',
                })}
              </span>
            )}
            {card.checklistTotal != null && card.checklistTotal > 0 && (
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <ListChecks size={11} strokeWidth={2} />
                {card.checklistDone ?? 0}/{card.checklistTotal}
              </span>
            )}
            {card.commentsCount != null && card.commentsCount > 0 && (
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <MessageSquare size={11} strokeWidth={2} />
                {card.commentsCount}
              </span>
            )}
            {card.attachmentsCount != null && card.attachmentsCount > 0 && (
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <Paperclip size={11} strokeWidth={2} />
                {card.attachmentsCount}
              </span>
            )}
            {card.estimate != null && (
              <span className="inline-flex items-center gap-0.5 tabular-nums">
                <AlertCircle size={11} strokeWidth={2} />
                {card.estimate}sp
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
